import React, { useState } from 'react';
import { Calendar, Clock, User, Tag, Search, Filter, ChevronRight, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { Footer } from '../components/Footer';
import { CompactHeader } from '../components/CompactHeader';

export default function NewsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const newsArticles = [
    {
      id: 1,
      title: 'Lagos State Judiciary Launches New E-Filing Portal for Enhanced Digital Access',
      excerpt: 'The Chief Judge of Lagos State officially launched an upgraded e-filing system that will streamline court processes and improve access to justice for all citizens.',
      content: 'The Lagos State Judiciary has unveiled a comprehensive digital transformation initiative with the launch of its new e-filing portal...',
      category: 'Technology',
      author: 'Public Affairs Office',
      date: '2024-01-15',
      readTime: '5 min read',
      image: 'modern technology courthouse',
      featured: true,
      tags: ['E-Filing', 'Digital Transformation', 'Access to Justice']
    },
    {
      id: 2,
      title: 'Virtual Hearing Platform Reduces Case Backlog by 35% in Six Months',
      excerpt: 'The implementation of virtual hearings has significantly improved court efficiency and reduced delays in case proceedings across all divisions.',
      content: 'Since the introduction of virtual hearing capabilities in July 2023, the Lagos State Judiciary has seen remarkable improvements...',
      category: 'Innovation',
      author: 'Case Management Office',
      date: '2024-01-12',
      readTime: '4 min read',
      image: 'virtual court hearing',
      featured: true,
      tags: ['Virtual Hearings', 'Efficiency', 'Case Management']
    },
    {
      id: 3,
      title: 'Chief Judge Inaugurates Three New Magistrate Courts in Emerging Areas',
      excerpt: 'Expanding judicial access to underserved communities, the new courts in Lekki, Ajah, and Ibeju-Lekki begin operations immediately.',
      content: 'In a ceremony attended by legal luminaries and community leaders, the Chief Judge of Lagos State inaugurated three new magistrate courts...',
      category: 'Infrastructure',
      author: 'Administrative Office',
      date: '2024-01-10',
      readTime: '3 min read',
      image: 'new courthouse opening',
      featured: false,
      tags: ['New Courts', 'Infrastructure', 'Community Access']
    },
    {
      id: 4,
      title: 'Lagos Judiciary Partners with Tech Companies for AI-Powered Case Management',
      excerpt: 'A groundbreaking partnership aims to leverage artificial intelligence for improved case scheduling, document management, and judicial analytics.',
      content: 'The Lagos State Judiciary has entered into strategic partnerships with leading technology companies to implement AI solutions...',
      category: 'Technology',
      author: 'Innovation Team',
      date: '2024-01-08',
      readTime: '6 min read',
      image: 'artificial intelligence technology',
      featured: false,
      tags: ['Artificial Intelligence', 'Case Management', 'Innovation']
    },
    {
      id: 5,
      title: 'Multi-Door Courthouse Celebrates 1000th Successful Mediation',
      excerpt: 'The Alternative Dispute Resolution center reaches a significant milestone, demonstrating the effectiveness of non-adversarial conflict resolution.',
      content: 'The Multi-Door Courthouse of Lagos State has achieved a remarkable milestone with its 1000th successful mediation...',
      category: 'ADR',
      author: 'ADR Center',
      date: '2024-01-05',
      readTime: '4 min read',
      image: 'mediation session',
      featured: false,
      tags: ['Mediation', 'ADR', 'Milestone']
    },
    {
      id: 6,
      title: 'Lagos Courts Implement New Environmental Sustainability Measures',
      excerpt: 'Going green: The judiciary introduces paperless processes, solar power, and sustainable practices across all court facilities.',
      content: 'As part of its commitment to environmental responsibility, the Lagos State Judiciary has implemented comprehensive sustainability measures...',
      category: 'Environment',
      author: 'Facilities Management',
      date: '2024-01-03',
      readTime: '3 min read',
      image: 'green sustainable building',
      featured: false,
      tags: ['Sustainability', 'Environment', 'Green Initiative']
    },
    {
      id: 7,
      title: 'Annual Judicial Conference Focuses on Criminal Justice Reform',
      excerpt: 'Legal experts, judges, and stakeholders gather to discuss innovative approaches to criminal justice and rehabilitation.',
      content: 'The Lagos State Judiciary hosted its annual conference with focus on comprehensive criminal justice reform...',
      category: 'Conference',
      author: 'Conference Committee',
      date: '2023-12-28',
      readTime: '5 min read',
      image: 'judicial conference',
      featured: false,
      tags: ['Conference', 'Criminal Justice', 'Reform']
    },
    {
      id: 8,
      title: 'Lagos Introduces Specialized Commercial Courts for IP Disputes',
      excerpt: 'New intellectual property courts will handle complex IP cases, supporting Lagos\' position as a commercial hub.',
      content: 'To support the growing technology and creative industries in Lagos, the state judiciary has established specialized courts...',
      category: 'Commercial',
      author: 'Commercial Court',
      date: '2023-12-25',
      readTime: '4 min read',
      image: 'intellectual property law',
      featured: false,
      tags: ['Intellectual Property', 'Commercial Law', 'Specialization']
    }
  ];

  const announcements = [
    {
      title: 'Court Holiday Schedule',
      date: '2024-01-20',
      description: 'Courts will be closed on January 26th for Democracy Day. Emergency matters will be handled by duty judges.',
      type: 'Schedule',
      urgent: false
    },
    {
      title: 'E-Filing System Maintenance',
      date: '2024-01-18',
      description: 'Scheduled maintenance on January 25th from 2:00 AM - 6:00 AM. System will be temporarily unavailable.',
      type: 'Technical',
      urgent: true
    },
    {
      title: 'New Filing Fee Structure',
      date: '2024-01-16',
      description: 'Updated court filing fees effective February 1st, 2024. View complete fee schedule in legal resources.',
      type: 'Policy',
      urgent: false
    },
    {
      title: 'Judicial Training Program',
      date: '2024-01-14',
      description: 'Applications open for the 2024 Judicial Training Program. Deadline for submissions: February 15th.',
      type: 'Training',
      urgent: false
    }
  ];

  const upcomingEvents = [
    {
      title: 'Lagos Legal Week 2024',
      date: '2024-02-15',
      time: '9:00 AM',
      location: 'Eko Hotel & Suites, Victoria Island',
      description: 'Annual gathering of legal professionals, judiciary, and stakeholders',
      type: 'Conference'
    },
    {
      title: 'Public Court Tour',
      date: '2024-02-10',
      time: '10:00 AM',
      location: 'High Court Complex, Igbosere',
      description: 'Educational tour for students and members of the public',
      type: 'Public Event'
    },
    {
      title: 'Family Court Sensitization Workshop',
      date: '2024-02-08',
      time: '2:00 PM',
      location: 'Family Court, Ikeja',
      description: 'Workshop on family court procedures and alternative dispute resolution',
      type: 'Workshop'
    },
    {
      title: 'Commercial Law Symposium',
      date: '2024-02-05',
      time: '9:30 AM',
      location: 'Commercial Court, Ikeja',
      description: 'Symposium on recent developments in commercial law and practice',
      type: 'Symposium'
    }
  ];

  const categories = [
    { value: 'all', label: 'All News', count: newsArticles.length },
    { value: 'Technology', label: 'Technology', count: newsArticles.filter(n => n.category === 'Technology').length },
    { value: 'Innovation', label: 'Innovation', count: newsArticles.filter(n => n.category === 'Innovation').length },
    { value: 'Infrastructure', label: 'Infrastructure', count: newsArticles.filter(n => n.category === 'Infrastructure').length },
    { value: 'ADR', label: 'ADR', count: newsArticles.filter(n => n.category === 'ADR').length },
    { value: 'Commercial', label: 'Commercial', count: newsArticles.filter(n => n.category === 'Commercial').length }
  ];

  const filteredArticles = selectedCategory === 'all' 
    ? newsArticles.filter(article => 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : newsArticles.filter(article => 
        article.category === selectedCategory &&
        (article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
         article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
      );

  const featuredArticles = newsArticles.filter(article => article.featured);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <CompactHeader  />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-800 via-blue-700 to-blue-600 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              News & Updates
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Stay informed with the latest news, announcements, and developments from Lagos State Judiciary
            </p>
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  placeholder="Search news and updates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white/90 border-white/50"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured News */}
      {featuredArticles.length > 0 && (
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-12 text-center">
              Featured News
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {featuredArticles.map((article, index) => (
                <Card key={article.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow group">
                  <div className="aspect-video bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 overflow-hidden">
                    <ImageWithFallback
                      src={`https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=800&h=400&fit=crop`}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <Badge variant="default">{article.category}</Badge>
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(article.date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Clock className="h-4 w-4 mr-1" />
                        {article.readTime}
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {article.title}
                    </h3>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <User className="h-4 w-4 mr-1" />
                        {article.author}
                      </div>
                      <Button variant="ghost" size="sm" className="group-hover:text-blue-600 dark:group-hover:text-blue-400">
                        Read More
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Announcements & Upcoming Events */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Important Announcements */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                Important Announcements
              </h2>
              
              <div className="space-y-4">
                {announcements.map((announcement, index) => (
                  <Card key={index} className={`border-0 shadow-lg ${announcement.urgent ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-blue-500'}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-gray-900 dark:text-white">
                          {announcement.title}
                        </h3>
                        <div className="flex items-center space-x-2">
                          {announcement.urgent && (
                            <Badge variant="destructive" className="text-xs">Urgent</Badge>
                          )}
                          <Badge variant="outline" className="text-xs">{announcement.type}</Badge>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
                        {announcement.description}
                      </p>
                      
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(announcement.date).toLocaleDateString()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Upcoming Events */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                Upcoming Events
              </h2>
              
              <div className="space-y-4">
                {upcomingEvents.map((event, index) => (
                  <Card key={index} className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-gray-900 dark:text-white">
                          {event.title}
                        </h3>
                        <Badge variant="secondary" className="text-xs">{event.type}</Badge>
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                        {event.description}
                      </p>
                      
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {new Date(event.date).toLocaleDateString()} at {event.time}
                        </div>
                        <div className="flex items-center">
                          <Tag className="h-4 w-4 mr-2" />
                          {event.location}
                        </div>
                      </div>
                      
                      <Button variant="outline" size="sm" className="mt-4">
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All News Articles */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              All News Articles
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Browse our complete collection of news and updates
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <Button
                key={category.value}
                variant={selectedCategory === category.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category.value)}
                className="flex items-center space-x-2"
              >
                <span>{category.label}</span>
                <Badge variant="secondary" className="text-xs">
                  {category.count}
                </Badge>
              </Button>
            ))}
          </div>

          {/* News Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <Card key={article.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow group">
                <div className="aspect-video bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 overflow-hidden">
                  <ImageWithFallback
                    src={`https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=600&h=300&fit=crop`}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <Badge variant="outline" className="text-xs">{article.category}</Badge>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="h-3 w-3 mr-1" />
                      {article.readTime}
                    </div>
                  </div>
                  
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed line-clamp-3">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(article.date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      {article.author}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mt-3">
                    {article.tags.slice(0, 2).map((tag, tagIndex) => (
                      <Badge key={tagIndex} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Load More */}
          <div className="text-center mt-12">
            <Button variant="outline" size="lg">
              Load More Articles
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl text-center">
          <h2 className="text-3xl font-bold mb-6">
            Stay Updated
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter and get the latest news and updates delivered to your inbox
          </p>
          <div className="max-w-md mx-auto flex gap-2">
            <Input
              placeholder="Enter your email address"
              className="bg-white/90 border-white/50 text-gray-900"
            />
            <Button variant="secondary">
              Subscribe
            </Button>
          </div>
        </div>
      </section>
       <Footer />
    </div>
  );
}